import Phaser from 'phaser';
import { WORLD } from '../world.ts';
import { getBeyDef, artBase } from '../game/stats.ts';
import { sound } from '../audio/sound.ts';
import { backdropGlow } from './TitleScene.ts';
import type { FinishType } from './BattleScene.ts';

interface ResultData {
  playerWon: boolean;
  finish: FinishType;
  playerId: string;
  opponentId: string;
  playerScore: number;
  oppScore: number;
  mode: 'exhibition' | 'tournament' | 'versus';
}

const FINISH_LABEL: Record<FinishType, string> = {
  burst: 'Burst Finish',
  ringout: 'Ring-Out',
  spinout: 'Spin-Out',
};

export class ResultsScene extends Phaser.Scene {
  private champ?: Phaser.GameObjects.Sprite;

  constructor() {
    super('Results');
  }

  create(data: ResultData): void {
    const { width, height } = WORLD;
    this.cameras.main.setBackgroundColor('#05060a');
    backdropGlow(this);

    const won = data.playerWon;
    const versus = data.mode === 'versus';
    // In versus someone always wins — play the victory sting either way.
    this.time.delayedCall(120, () => (won || versus ? sound.win() : sound.lose()));
    const headline = versus ? (won ? 'PLAYER 1 WINS!' : 'PLAYER 2 WINS!') : won ? 'YOU WIN!' : 'DEFEATED';
    this.add
      .text(width / 2, height * 0.34, headline, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: versus ? '76px' : '96px',
        fontStyle: 'bold',
        color: won ? '#ffd23b' : versus ? '#5ad6ff' : '#ff5b5b',
        stroke: '#000',
        strokeThickness: 10,
      })
      .setOrigin(0.5);

    const winner = won ? getBeyDef(data.playerId) : getBeyDef(data.opponentId);
    // Spinning victory spotlight of the winning bey.
    this.champ = this.add
      .sprite(width / 2, height * 0.16, `bey-${artBase(winner)}-face`)
      .setDisplaySize(132, 132);

    const subtitle = versus
      ? `${winner.name} takes the match`
      : `${winner.name} wins the match`;
    this.add
      .text(width / 2, height * 0.46, subtitle, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '32px',
        fontStyle: 'bold',
        color: '#cfe0ff',
      })
      .setOrigin(0.5);

    // Final score line, e.g. "2 — 1   ·   by Ring-Out".
    this.add
      .text(
        width / 2,
        height * 0.54,
        `${data.playerScore} — ${data.oppScore}   ·   clinched by ${FINISH_LABEL[data.finish]}`,
        {
          fontFamily: 'Trebuchet MS, sans-serif',
          fontSize: '24px',
          color: '#8aa0c8',
        },
      )
      .setOrigin(0.5);

    const tournament = data.mode === 'tournament';
    const promptText = tournament
      ? won
        ? 'ENTER  continue the run'
        : 'ENTER  see the bracket'
      : versus
        ? 'ENTER  new versus        T  title'
        : 'ENTER  rematch        T  title';
    const prompt = this.add
      .text(width / 2, height * 0.66, promptText, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '28px',
        color: '#8aa0c8',
      })
      .setOrigin(0.5);
    this.tweens.add({ targets: prompt, alpha: 0.3, duration: 700, yoyo: true, repeat: -1 });

    this.input.keyboard!.once('keydown-ENTER', () => {
      if (tournament) this.scene.start('Tournament', { matchResult: won });
      else this.scene.start('Select', { mode: versus ? 'versus' : 'exhibition' });
    });
    this.input.keyboard!.once('keydown-T', () => this.scene.start('Title'));
  }

  update(_t: number, delta: number): void {
    if (this.champ) this.champ.angle += (delta / 1000) * 220;
  }
}
